import React from 'react';
import { DATA_LAYERS } from '../../../lib/dataLayers';
import { useSoilCarbon } from '../../../hooks/useSoilCarbon';
import { tonsPerAcre } from '../../../lib/units';
import { LayerHeader } from './LayerHeader';
import { StatCard } from './StatCard';
import { AboutLayer } from './AboutLayer';
import {
  LayerErrorAlert,
  LayerLiveBadge,
  SkeletonStatCardGrid,
} from './contentPrimitives';

interface SoilCarbonContentProps {
  millCode: string | null;
  driveTimeMinutes: number;
  // Depth lives in DetailView so the map raster and these stats stay in sync;
  // this component is now purely prop-driven.
  depth: 30 | 100;
  onDepthChange: (depth: 30 | 100) => void;
}

const DEPTHS: Array<{ value: 30 | 100; label: string }> = [
  { value: 30, label: '0-30 cm' },
  { value: 100, label: '0-100 cm' },
];

export const SoilCarbonContent: React.FC<SoilCarbonContentProps> = ({
  millCode,
  driveTimeMinutes,
  depth,
  onDepthChange,
}) => {
  const layer = DATA_LAYERS.soil_carbon;
  const { data, isLoading, error } = useSoilCarbon(depth, millCode, driveTimeMinutes);

  const fmt = (n: number) => `${tonsPerAcre(n).toFixed(1)} ${layer.units}`;

  return (
    <div className="space-y-6">
      <LayerHeader layer={layer} />

      {/* Dataset metadata — surfaced here because the layer dropdown's
          "100m / 2022" is otherwise hidden once a layer is selected. */}
      <div className="text-xs font-roboto-mono uppercase tracking-wider text-neutral-500">
        100 m resolution &middot; 2022
      </div>

      <AboutLayer>
        <p>
          Soil organic carbon (SOC) is the carbon held in the soil itself, separate from
          above-ground biomass. A larger SOC pool indicates more carbon kept out of the
          atmosphere and generally a healthier, more resilient soil structure that is
          better at holding water, supporting tree growth, and resisting erosion.
        </p>
        <p>
          For a sourcing decision, supply sheds with deeper SOC stocks are typically
          more productive over the long term and represent lower carbon-loss risk if
          the soil is left undisturbed during harvest.
        </p>
        <p>
          Values are reported at two depths &mdash; <strong>0&ndash;30&thinsp;cm</strong>
          (the active rooting zone, most exposed to disturbance) and{' '}
          <strong>0&ndash;100&thinsp;cm</strong> (the full soil profile, slower to
          release carbon). Toggle depth to compare.
        </p>
      </AboutLayer>

      {/* Depth toggle + hint */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-body-sm font-roboto text-neutral-400">Depth:</span>
          <div className="flex rounded-[10px] overflow-hidden border border-figma-mint">
            {DEPTHS.map(d => (
              <button
                key={d.value}
                onClick={() => onDepthChange(d.value)}
                className={`px-3 py-1.5 text-sm font-roboto-mono font-medium transition-colors ${
                  depth === d.value
                    ? 'bg-figma-mint text-[#070807]'
                    : 'bg-transparent text-figma-text hover:bg-figma-mint/10'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs font-roboto italic text-neutral-500 leading-relaxed">
          {depth === 30
            ? '0\u201330 cm is the topsoil \u2014 where most root activity and rapid carbon cycling happen. Most carbon-credit methodologies focus on this layer.'
            : '0\u2013100 cm includes the subsoil. Typically a larger total pool than topsoil alone, but it cycles much more slowly and is less responsive to land-use change.'}
        </p>
      </div>

      {isLoading && <SkeletonStatCardGrid />}

      {!isLoading && error && !data && (
        <LayerErrorAlert layer={layer} details={error} />
      )}

      {!isLoading && data && (
        <>
          <LayerLiveBadge />
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Mean" value={fmt(data.mean)} />
            <StatCard label="Std. dev" value={fmt(data.std)} />
            <StatCard label="Minimum" value={fmt(data.min)} />
            <StatCard label="Maximum" value={fmt(data.max)} />
          </div>
          <p className="text-xs font-roboto italic text-neutral-500 leading-relaxed">
            Mean is the average soil carbon stock across the 0&ndash;{data.depthCm} cm profile within
            the supply shed. Std. dev. captures how variable soils are across the area &mdash; a wide
            spread suggests mixed land cover or soil types. Min/max bound the range of pixel values
            inside the boundary.
          </p>
        </>
      )}
    </div>
  );
};
